import { useMemo, useState } from 'react';
import { BuildingStorefrontIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
    PickerOptionButton,
    SearchPickerInput,
    SearchPickerList,
    type SearchPickerStatus
} from '../../../shared/SearchPicker';
import { useAddOrthodontistPractice } from '../../../../hooks/useOrthodontistPractices';
import { usePracticeSearch } from '../../../../hooks/usePracticeSearch';
import { useToast } from '../../../../providers/ToastProvider';
import type { PracticeListItem } from '../../../../services/accounts';
import type { OrthodontistPracticeSummary } from '../../../../services/orthodontistAccounts';
import { apiErrorMessage } from '../../../../utils/apiError';
import { RemoveOrthodontistPracticeModal } from './RemoveOrthodontistPracticeModal';

interface OrthodontistLinkedPracticesCardProps {
    orthodontistId: string;
    orthodontistName: string;
    practices: OrthodontistPracticeSummary[];
}

function addPracticeStatus(isLoading: boolean, matchCount: number): SearchPickerStatus | null {
    if (isLoading) return { text: 'Searching…' };
    return matchCount === 0 ? { text: 'No matching practices found.' } : null;
}

interface AddPracticeFieldProps {
    orthodontistId: string;
    orthodontistName: string;
    existingPracticeIds: Set<string>;
    onClose: () => void;
}

// A practice can belong to at most one org (DB-enforced), so picking one
// already linked elsewhere surfaces the backend's 409 message via the toast.
function AddPracticeField({
    orthodontistId,
    orthodontistName,
    existingPracticeIds,
    onClose
}: AddPracticeFieldProps) {
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const { results, isLoading } = usePracticeSearch(search, true);
    const matches = results.filter((practice) => !existingPracticeIds.has(practice.id));
    const addPractice = useAddOrthodontistPractice();
    const status = addPracticeStatus(isLoading, matches.length);

    const handlePick = async (practice: PracticeListItem) => {
        onClose();
        try {
            await addPractice.mutateAsync({ orthodontistId, practiceId: practice.id });
            showToast(`${practice.name} linked to ${orthodontistName}`, 'success');
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to link practice to org'), 'error');
        }
    };

    return (
        <div>
            <SearchPickerInput
                search={search}
                onSearchChange={setSearch}
                open
                onOpen={() => {}}
                onClose={onClose}
                placeholder="Search practices by name"
            />
            {status ? (
                <div
                    className={`mt-2 text-[12px] ${status.isError ? 'text-red-600' : 'text-gray-400'}`}
                >
                    {status.text}
                </div>
            ) : (
                <div className="max-h-[260px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 mt-2">
                    {matches.map((practice) => (
                        <PickerOptionButton
                            key={practice.id}
                            option={{
                                key: practice.id,
                                title: practice.name,
                                subline: practice.doctorName ? `Dr. ${practice.doctorName}` : null,
                                onPick: () => handlePick(practice)
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// Admin-managed relationship (like Users), unlike the read-only cards above -
// a practice belongs to at most one org, DB-enforced via orthodontist_practice.
export function OrthodontistLinkedPracticesCard({
    orthodontistId,
    orthodontistName,
    practices
}: OrthodontistLinkedPracticesCardProps) {
    const [isAdding, setIsAdding] = useState(false);
    const [removeTarget, setRemoveTarget] = useState<OrthodontistPracticeSummary | null>(null);

    const existingPracticeIds = useMemo(
        () => new Set(practices.map((practice) => practice.id)),
        [practices]
    );

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] overflow-hidden shrink-0">
            <div className="border-b border-gray-100 flex items-center justify-between gap-2.5 px-5 h-12">
                <div className="flex items-center gap-2.5">
                    <BuildingStorefrontIcon className="h-4 w-4 text-gray-400" />
                    <h3 className="text-[13px] font-bold text-gray-800">Linked practices</h3>
                    <span className="text-[12px] text-gray-400">{practices.length} practices</span>
                </div>
                <button
                    type="button"
                    onClick={() => setIsAdding((open) => !open)}
                    className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-700 hover:text-gray-900"
                >
                    <PlusIcon className="h-3.5 w-3.5" />
                    Add practice
                </button>
            </div>
            <div className="p-5 flex flex-col gap-3">
                {isAdding && (
                    <AddPracticeField
                        orthodontistId={orthodontistId}
                        orthodontistName={orthodontistName}
                        existingPracticeIds={existingPracticeIds}
                        onClose={() => setIsAdding(false)}
                    />
                )}
                <SearchPickerList
                    emptyText="No practices linked to this org yet."
                    rows={practices.map((practice) => ({
                        key: practice.id,
                        title: practice.name,
                        subline: `Dr. ${practice.drFirstName} ${practice.drLastName}`,
                        onRemove: () => setRemoveTarget(practice),
                        removeLabel: `Unlink ${practice.name}`
                    }))}
                />
            </div>
            {removeTarget && (
                <RemoveOrthodontistPracticeModal
                    orthodontistId={orthodontistId}
                    orthodontistName={orthodontistName}
                    practice={removeTarget}
                    onClose={() => setRemoveTarget(null)}
                />
            )}
        </div>
    );
}
